class ProcessCenterSlider {
  constructor(container) {
    this.container = container;
    this.slider = container.querySelector('.process-slider');
    this.slides = Array.from(container.querySelectorAll('.process-slide'));
    this.prevButton = container.querySelector('.process-slider-nav.prev');
    this.nextButton = container.querySelector('.process-slider-nav.next');
    this.slideCount = this.slides.length;
    this.isAnimating = false;
    this.autoplayInterval = null;
    this.positionUpdateTimeout = null;
    
    try {
      // 为每个实例创建唯一ID，加入section ID作为前缀
      const sectionId = container.closest('.process-slider-section').getAttribute('data-section-id');
      this.instanceId = `process-slider-${sectionId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      this.container.setAttribute('data-slider-id', this.instanceId);
      
      // 查找初始活动幻灯片的索引
      let activeIndex = 0;
      this.slides.forEach((slide, index) => {
        if (slide.classList.contains('active')) {
          activeIndex = index;
        }
        // 为每个幻灯片添加实例ID
        slide.setAttribute('data-slider-id', this.instanceId);
      });
      this.currentIndex = activeIndex;

      // 创建Intersection Observer来监测轮播图的可见性
      this.createVisibilityObserver();

      // 初始化位置更新
      this.initPositionUpdate();

      if (this.slideCount > 0) {
        // 等待所有图片加载完成后再初始化
        this.waitForImages().then(() => {
          this.init();
          this.updateButtonPositions();
        }).catch(error => {
          console.error('等待图片加载失败:', error);
          // 即使图片加载失败也继续初始化
          this.init();
          this.updateButtonPositions();
        });
      } else {
        console.warn('没有找到幻灯片元素');
      }
      
      // 将实例存储在容器上
      container.processCenterSliderInstance = this;
      // console.log('轮播实例初始化成功，ID:', this.instanceId);
      
    } catch (error) {
      console.error('轮播图初始化失败:', error);
    }
  }

  // 创建可见性观察器
  createVisibilityObserver() {
    try {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // 当轮播图进入视口时，更新按钮位置
            this.updateButtonPositions();
            // 设置定期检查位置
            this.startPositionCheck();
          } else {
            // 当轮播图离开视口时，停止定期检查
            this.stopPositionCheck();
          }
        });
      }, {
        threshold: 0.1 // 当10%的内容可见时触发
      });

      // 开始观察轮播图容器
      this.observer.observe(this.container);
    } catch (error) {
      console.error('创建可见性观察器失败:', error);
    }
  }

  // 初始化位置更新
  initPositionUpdate() {
    // DOM加载完成后更新位置
    if (document.readyState === 'complete') {
      this.updateButtonPositions();
      this.startPositionCheck();
    } else {
      window.addEventListener('load', () => {
        this.updateButtonPositions();
        this.startPositionCheck();
      });
    }

    // 监听滚动事件
    window.addEventListener('scroll', () => {
      if (this.positionUpdateTimeout) {
        clearTimeout(this.positionUpdateTimeout);
      }
      this.positionUpdateTimeout = setTimeout(() => {
        this.updateButtonPositions();
      }, 100);
    }, { passive: true });

    // 监听窗口大小变化
    window.addEventListener('resize', () => {
      if (this.positionUpdateTimeout) {
        clearTimeout(this.positionUpdateTimeout);
      }
      this.positionUpdateTimeout = setTimeout(() => {
        this.updateButtonPositions();
      }, 100);
    }, { passive: true });
  }

  // 开始定期检查位置
  startPositionCheck() {
    if (this.positionCheckInterval) {
      clearInterval(this.positionCheckInterval);
    }
    // 每500ms检查一次位置
    this.positionCheckInterval = setInterval(() => {
      this.updateButtonPositions();
    }, 500);
  }

  // 停止定期检查位置
  stopPositionCheck() {
    if (this.positionCheckInterval) {
      clearInterval(this.positionCheckInterval);
      this.positionCheckInterval = null;
    }
  }

  // 等待所有图片加载完成
  waitForImages() {
    return new Promise((resolve, reject) => {
      const images = Array.from(this.container.querySelectorAll('.process-slide img'));
      if (images.length === 0) {
        resolve();
        return;
      }

      let loadedImages = 0;
      const totalImages = images.length;

      const imageLoaded = () => {
        loadedImages++;
        if (loadedImages === totalImages) {
          resolve();
        }
      };

      images.forEach(img => {
        if (img.complete) {
          imageLoaded();
        } else {
          img.onload = imageLoaded;
          img.onerror = () => {
            console.warn('图片加载失败:', img.src);
            imageLoaded();
          };
        }
      });

      // 设置超时，防止图片加载时间过长
      setTimeout(() => {
        if (loadedImages < totalImages) {
          console.warn('部分图片加载超时，继续初始化');
          resolve();
        }
      }, 3000);
    });
  }

  init() {
    try {
      // 为导航按钮添加实例ID
      if (this.prevButton) {
        this.prevButton.setAttribute('data-slider-id', this.instanceId);
        this.prevButton.removeEventListener('click', this.prevHandler);
        this.prevHandler = (e) => this.prevSlide(e);
        this.prevButton.addEventListener('click', this.prevHandler);
      }
      
      if (this.nextButton) {
        this.nextButton.setAttribute('data-slider-id', this.instanceId);
        this.nextButton.removeEventListener('click', this.nextHandler);
        this.nextHandler = (e) => this.nextSlide(e);
        this.nextButton.addEventListener('click', this.nextHandler);
      }
      
      // 添加窗口大小变化监听
      this.resizeHandler = this.handleResize.bind(this);
      window.addEventListener('resize', this.resizeHandler);
      
      // 鼠标悬停暂停轮播
      this.container.addEventListener('mouseenter', () => this.stopAutoplay());
      this.container.addEventListener('mouseleave', () => this.startAutoplay());

      // 使用 MutationObserver 监听DOM变化
      this.observeSliderChanges();
      
      // console.log('轮播图初始化完成，总共', this.slideCount, '张图片');
      
      // 开始自动播放
      this.startAutoplay();
      
    } catch (error) {
      console.error('轮播图事件绑定失败:', error);
    }
  }

  // 监听轮播图DOM变化
  observeSliderChanges() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' || mutation.type === 'childList') {
          this.updateButtonPositions();
        }
      });
    });

    observer.observe(this.slider, {
      attributes: true,
      childList: true,
      subtree: true
    });
  }
  
  // 更新按钮位置
  updateButtonPositions() {
    try {
      if (!this.prevButton || !this.nextButton || this.slides.length === 0) return;

      // 获取当前活动幻灯片
      const activeSlide = this.slides[this.currentIndex];
      if (!activeSlide) return;

      // 检查轮播图是否可见
      const containerRect = this.container.getBoundingClientRect();
      if (containerRect.top > window.innerHeight || containerRect.bottom < 0) {
        return; // 如果不在视口内，不更新位置
      }

      // 获取活动幻灯片的位置和尺寸
      const slideRect = activeSlide.getBoundingClientRect();

      // 计算按钮的垂直位置（相对于容器）
      const verticalCenter = slideRect.top + slideRect.height / 2 - containerRect.top;

      // 更新按钮的垂直位置
      if (this.prevButton) {
        this.prevButton.style.top = `${verticalCenter}px`;
      }

      if (this.nextButton) {
        this.nextButton.style.top = `${verticalCenter}px`;
      }

      // console.log('按钮垂直位置已更新:', verticalCenter);
    } catch (error) {
      console.error('更新按钮位置失败:', error);
    }
  }

  // 窗口大小变化处理
  handleResize() {
    // 使用 requestAnimationFrame 来优化性能
    if (this.resizeTimeout) {
      window.cancelAnimationFrame(this.resizeTimeout);
    }
    this.resizeTimeout = window.requestAnimationFrame(() => {
      this.updateButtonPositions();
    });
  }

  prevSlide(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (this.isAnimating || this.slideCount <= 1) return false;
    
    // console.log('上一张');
    const prevIndex = (this.currentIndex - 1 + this.slideCount) % this.slideCount;
    this.goToSlide(prevIndex);
    
    return false;
  }

  nextSlide(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (this.isAnimating || this.slideCount <= 1) return false;
    
    // console.log('下一张');
    const nextIndex = (this.currentIndex + 1) % this.slideCount;
    this.goToSlide(nextIndex);
    
    return false;
  }

  goToSlide(targetIndex) {
    if (this.isAnimating || targetIndex === this.currentIndex) return;
    
    this.isAnimating = true;
    
    // 计算前后幻灯片的索引
    const prevIndex = (targetIndex - 1 + this.slideCount) % this.slideCount;
    const nextIndex = (targetIndex + 1) % this.slideCount;
    
    // 只操作当前实例的幻灯片
    this.slides.forEach(slide => {
      if (slide.getAttribute('data-slider-id') === this.instanceId) {
        slide.classList.remove('active', 'prev', 'next');
      }
    });
    
    this.slides[prevIndex].classList.add('prev');
    this.slides[targetIndex].classList.add('active');
    this.slides[nextIndex].classList.add('next');
    
    this.currentIndex = targetIndex;
    
    // 更新按钮位置
    this.updateButtonPositions();
    
    setTimeout(() => {
      this.isAnimating = false;
    }, 600);
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, 5000);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }
  
  // 清理函数，在组件销毁时调用
  destroy() {
    try {
      this.stopAutoplay();
      this.stopPositionCheck();
      if (this.observer) {
        this.observer.disconnect();
      }
      window.removeEventListener('resize', this.resizeHandler);
      
      // 移除事件监听器和属性
      if (this.prevButton && this.prevHandler) {
        this.prevButton.removeEventListener('click', this.prevHandler);
        this.prevButton.removeAttribute('data-slider-id');
      }
      if (this.nextButton && this.nextHandler) {
        this.nextButton.removeEventListener('click', this.nextHandler);
        this.nextButton.removeAttribute('data-slider-id');
      }
      
      // 移除所有幻灯片的实例ID
      this.slides.forEach(slide => {
        slide.removeAttribute('data-slider-id');
        slide.classList.remove('active', 'prev', 'next');
      });
      
      // 移除容器的实例ID和引用
      this.container.removeAttribute('data-slider-id');
      delete this.container.processCenterSliderInstance;
      
      // console.log('轮播图实例已成功销毁');
    } catch (error) {
      console.error('轮播图销毁失败:', error);
    }
  }
}

// 初始化函数
function initProcessSliders() {
  try {
    const containers = document.querySelectorAll('.process-slider-container');
    
    containers.forEach(container => {
      // 如果已经有实例，先销毁
      if (container.processCenterSliderInstance) {
        container.processCenterSliderInstance.destroy();
      }
      
      // 创建新实例
      new ProcessCenterSlider(container);
    });
  } catch (error) {
    console.error('轮播图初始化失败:', error);
  }
}

// 页面加载完成时初始化
document.addEventListener('DOMContentLoaded', initProcessSliders);

// Shopify主题编辑器支持
document.addEventListener('shopify:section:load', function(event) {
  try {
    const containers = event.target.querySelectorAll('.process-slider-container');
    containers.forEach(container => {
      if (container.processCenterSliderInstance) {
        container.processCenterSliderInstance.destroy();
      }
      new ProcessCenterSlider(container);
    });
  } catch (error) {
    console.error('Shopify编辑器中轮播图初始化失败:', error);
  }
});

// 如果DOM已经加载完成，直接初始化
if (document.readyState === 'interactive' || document.readyState === 'complete') {
  initProcessSliders();
} 